import React, { useEffect, useState, useCallback } from 'react';
import { activationAPI } from '../services/api';
import '../styles/ActivationEmailsAdmin.css';

const RULE_KEY_LABELS = {
  registered_no_course:     'لم ينشئ دورة بعد التسجيل',
  course_no_lecture:        'أنشأ دورة ولم يضف محاضرة',
  course_ready_no_students: 'دورة ومحاضرة بدون طلاب',
  lectures_no_exam:         'محاضرات بدون امتحان',
  inactive_teacher_help:    'معلم غير نشط',
};

const TEMPLATE_VARS = [
  { v: '{{teacher_name}}',  d: 'اسم المعلم' },
  { v: '{{dashboard_url}}', d: 'رابط لوحة التحكم' },
  { v: '{{course_title}}',  d: 'عنوان الدورة' },
  { v: '{{platform_name}}', d: 'اسم المنصة' },
  { v: '{{support_email}}', d: 'بريد الدعم' },
  { v: '{{cta_label}}',     d: 'نص زر الإجراء' },
  { v: '{{cta_url}}',       d: 'رابط زر الإجراء' },
];

// Substitute preview variables with sample values
function previewHtml(html) {
  return html
    .replace(/\{\{teacher_name\}\}/g,  'أحمد محمد')
    .replace(/\{\{teacherName\}\}/g,   'أحمد محمد')
    .replace(/\{\{dashboard_url\}\}/g, 'https://dashboard.daress.app')
    .replace(/\{\{dashboardLink\}\}/g, 'https://dashboard.daress.app')
    .replace(/\{\{course_title\}\}/g,  'دورة الرياضيات')
    .replace(/\{\{courseName\}\}/g,    'دورة الرياضيات')
    .replace(/\{\{platform_name\}\}/g, 'دارس')
    .replace(/\{\{support_email\}\}/g, 'support@daress.app')
    .replace(/\{\{cta_label\}\}/g,     'ابدأ الآن')
    .replace(/\{\{cta_url\}\}/g,       'https://dashboard.daress.app');
}

const Toast = ({ message, type, onClose }) => (
  <div className={`aem-toast aem-toast--${type}`} role="alert">
    <span>{message}</span>
    <button className="aem-toast-close" onClick={onClose}>✕</button>
  </div>
);

const ActivationEmailsAdmin = () => {
  const userRole = localStorage.getItem('userRole');
  const isAllowed = userRole === 'admin' || userRole === 'owner';

  const [rules, setRules]           = useState([]);
  const [logs, setLogs]             = useState([]);
  const [logsTotal, setLogsTotal]   = useState(0);
  const [loadingRules, setLoadingRules] = useState(true);
  const [loadingLogs, setLoadingLogs]   = useState(true);

  const [testEmail, setTestEmail]   = useState('');
  const [testingId, setTestingId]   = useState(null);

  const [editModal, setEditModal]   = useState(null);
  const [editForm, setEditForm]     = useState({});
  const [saving, setSaving]         = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const [logFilters, setLogFilters] = useState({ status: '', type: '', limit: 50 });

  const [toast, setToast]           = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchRules = useCallback(async () => {
    setLoadingRules(true);
    try {
      const res = await activationAPI.getRules();
      setRules(res.data || []);
    } catch {
      showToast('فشل تحميل القواعد', 'error');
    } finally {
      setLoadingRules(false);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      const params = { limit: logFilters.limit };
      if (logFilters.status) params.status = logFilters.status;
      if (logFilters.type)   params.type   = logFilters.type;
      const res  = await activationAPI.getLogs(params);
      const body = res.data;
      setLogs(body.logs ?? (Array.isArray(body) ? body : []));
      setLogsTotal(body.total ?? body.logs?.length ?? 0);
    } catch {
      showToast('فشل تحميل سجل الإيميلات', 'error');
    } finally {
      setLoadingLogs(false);
    }
  }, [logFilters]);

  useEffect(() => { fetchRules(); }, [fetchRules]);
  useEffect(() => { fetchLogs();  }, [fetchLogs]);

  const handleToggleEnabled = async (rule) => {
    try {
      const res = await activationAPI.updateRule(rule.id, { enabled: !rule.enabled });
      setRules(prev => prev.map(r => r.id === rule.id ? res.data : r));
      showToast(res.data.enabled ? 'تم تفعيل القاعدة' : 'تم إيقاف القاعدة');
    } catch {
      showToast('فشل تحديث القاعدة', 'error');
    }
  };

  const handleSendTest = async (ruleId) => {
    if (!testEmail.trim()) {
      showToast('أدخل بريد الاختبار أولاً', 'error');
      return;
    }
    setTestingId(ruleId);
    try {
      const res = await activationAPI.testRule(ruleId, { email: testEmail.trim() });
      showToast(res.data?.message || 'تم إرسال الإيميل التجريبي');
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'فشل الإرسال';
      showToast(`فشل: ${msg}`, 'error');
    } finally {
      setTestingId(null);
    }
  };

  const openEdit = (rule) => {
    setEditForm({
      enabled:           !!rule.enabled,
      delay_hours:       rule.delay_hours  ?? 24,
      max_sends:         rule.max_sends    ?? 2,
      brevo_template_id: rule.brevo_template_id ?? '',
      subject:           rule.subject      ?? '',
      body_html:         rule.body_html    ?? '',
      body_text:         rule.body_text    ?? '',
      cta_label:         rule.cta_label    ?? '',
      cta_url:           rule.cta_url      ?? '',
    });
    setShowPreview(false);
    setEditModal({ rule });
  };

  const handleSaveEdit = async () => {
    if (!editModal) return;
    setSaving(true);
    try {
      const payload = {
        enabled:           editForm.enabled,
        delay_hours:       Number(editForm.delay_hours),
        max_sends:         Number(editForm.max_sends),
        brevo_template_id: editForm.brevo_template_id ? Number(editForm.brevo_template_id) : null,
        subject:           editForm.subject,
        body_html:         editForm.body_html  || null,
        body_text:         editForm.body_text  || null,
        cta_label:         editForm.cta_label  || null,
        cta_url:           editForm.cta_url    || null,
      };
      const res = await activationAPI.updateRule(editModal.rule.id, payload);
      setRules(prev => prev.map(r => r.id === editModal.rule.id ? res.data : r));
      showToast('تم حفظ القاعدة بنجاح');
      setEditModal(null);
    } catch (err) {
      showToast(err.response?.data?.message || 'فشل الحفظ', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ---------- Access guard ----------
  if (!isAllowed) {
    return (
      <div className="aem-denied" dir="rtl">
        <div className="aem-denied-card">
          <span className="aem-denied-icon">🚫</span>
          <h2>ليس لديك صلاحية للوصول إلى هذه الصفحة.</h2>
          <p>هذه الصفحة مخصصة للمسؤولين فقط.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="aem-page" dir="rtl">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="aem-page-header">
        <h1 className="aem-page-title">رسائل تفعيل المعلمين</h1>
        <p className="aem-page-subtitle">إدارة قواعد الإرسال التلقائي لإيميلات التفعيل</p>
      </div>

      {/* Test email bar */}
      <div className="aem-test-bar">
        <label className="aem-test-label" htmlFor="aem-test-email-input">بريد الاختبار:</label>
        <input
          id="aem-test-email-input"
          className="aem-test-input"
          type="email"
          placeholder="أدخل البريد الإلكتروني لاختبار الإرسال"
          value={testEmail}
          onChange={e => setTestEmail(e.target.value)}
          dir="ltr"
        />
        <p className="aem-test-hint">اضغط "اختبار" في أي قاعدة لإرسال إيميل تجريبي إلى هذا البريد.</p>
      </div>

      {/* ====== Rules Section ====== */}
      <section className="aem-section">
        <h2 className="aem-section-title">قواعد الإرسال</h2>

        {loadingRules ? (
          <div className="aem-loading">جاري التحميل...</div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="aem-table-wrap aem-desktop-only">
              <table className="aem-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>مفتاح القاعدة</th>
                    <th>العنوان</th>
                    <th>مفعّل</th>
                    <th>التأخير</th>
                    <th>حد الإرسال</th>
                    <th>Template ID</th>
                    <th>إجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {rules.map(rule => (
                    <tr key={rule.id}>
                      <td>{rule.id}</td>
                      <td><code className="aem-rule-key">{rule.rule_key}</code></td>
                      <td>{rule.title}</td>
                      <td>
                        <label className="aem-toggle" title={rule.enabled ? 'إيقاف' : 'تفعيل'}>
                          <input
                            type="checkbox"
                            checked={!!rule.enabled}
                            onChange={() => handleToggleEnabled(rule)}
                          />
                          <span className="aem-toggle-slider" />
                        </label>
                      </td>
                      <td>{rule.delay_hours}h</td>
                      <td>{rule.max_sends}</td>
                      <td>{rule.brevo_template_id || '—'}</td>
                      <td>
                        <div className="aem-action-btns">
                          <button className="aem-btn aem-btn--edit" onClick={() => openEdit(rule)}>
                            تعديل
                          </button>
                          <button
                            className="aem-btn aem-btn--test"
                            onClick={() => handleSendTest(rule.id)}
                            disabled={testingId === rule.id}
                          >
                            {testingId === rule.id ? '...' : 'اختبار'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="aem-cards aem-mobile-only">
              {rules.map(rule => (
                <div key={rule.id} className="aem-rule-card">
                  <div className="aem-rule-card-header">
                    <div>
                      <div className="aem-rule-card-title">{rule.title}</div>
                      <code className="aem-rule-key">{rule.rule_key}</code>
                    </div>
                    <label className="aem-toggle">
                      <input
                        type="checkbox"
                        checked={!!rule.enabled}
                        onChange={() => handleToggleEnabled(rule)}
                      />
                      <span className="aem-toggle-slider" />
                    </label>
                  </div>
                  <div className="aem-rule-card-meta">
                    <span>تأخير: <strong>{rule.delay_hours}h</strong></span>
                    <span>حد: <strong>{rule.max_sends}</strong></span>
                    {rule.brevo_template_id && <span>Template: <strong>{rule.brevo_template_id}</strong></span>}
                  </div>
                  <div className="aem-rule-card-actions">
                    <button className="aem-btn aem-btn--edit" onClick={() => openEdit(rule)}>
                      تعديل الرسالة
                    </button>
                    <button
                      className="aem-btn aem-btn--test"
                      onClick={() => handleSendTest(rule.id)}
                      disabled={testingId === rule.id}
                    >
                      {testingId === rule.id ? 'جاري الإرسال...' : 'إرسال تجربة'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      {/* ====== Logs Section ====== */}
      <section className="aem-section">
        <div className="aem-section-header">
          <h2 className="aem-section-title">سجل الإيميلات</h2>
          <button className="aem-btn aem-btn--refresh" onClick={fetchLogs}>🔄 تحديث</button>
        </div>

        <div className="aem-filters">
          <select
            className="aem-filter-select"
            value={logFilters.status}
            onChange={e => setLogFilters(p => ({ ...p, status: e.target.value }))}
          >
            <option value="">كل الحالات</option>
            <option value="sent">مُرسَل</option>
            <option value="failed">فشل</option>
            <option value="skipped">تخطى</option>
          </select>

          <select
            className="aem-filter-select"
            value={logFilters.type}
            onChange={e => setLogFilters(p => ({ ...p, type: e.target.value }))}
          >
            <option value="">كل الأنواع</option>
            {Object.entries(RULE_KEY_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>

          <select
            className="aem-filter-select"
            value={logFilters.limit}
            onChange={e => setLogFilters(p => ({ ...p, limit: Number(e.target.value) }))}
          >
            <option value={25}>25 سجل</option>
            <option value={50}>50 سجل</option>
            <option value={100}>100 سجل</option>
          </select>
        </div>

        {loadingLogs ? (
          <div className="aem-loading">جاري التحميل...</div>
        ) : logs.length === 0 ? (
          <div className="aem-empty">لا توجد سجلات حتى الآن.</div>
        ) : (
          <>
            <p className="aem-logs-count">إجمالي: {logsTotal} سجل</p>

            {/* Desktop table */}
            <div className="aem-table-wrap aem-desktop-only">
              <table className="aem-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>البريد</th>
                    <th>النوع</th>
                    <th>الموضوع</th>
                    <th>الحالة</th>
                    <th>الخطأ</th>
                    <th>تاريخ الإرسال</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map(log => (
                    <tr key={log.id}>
                      <td>{log.id}</td>
                      <td dir="ltr" className="aem-log-email">{log.email}</td>
                      <td><code className="aem-rule-key">{log.type}</code></td>
                      <td className="aem-log-subject">{log.subject}</td>
                      <td>
                        <span className={`aem-badge aem-badge--${log.status}`}>
                          {log.status === 'sent' ? 'مُرسَل' : log.status === 'failed' ? 'فشل' : 'تخطى'}
                        </span>
                      </td>
                      <td className="aem-log-error">{log.error_message || '—'}</td>
                      <td dir="ltr">
                        {log.sent_at
                          ? new Date(log.sent_at).toLocaleString('ar-EG')
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="aem-cards aem-mobile-only">
              {logs.map(log => (
                <div key={log.id} className={`aem-log-card aem-log-card--${log.status}`}>
                  <div className="aem-log-card-top">
                    <span className={`aem-badge aem-badge--${log.status}`}>
                      {log.status === 'sent' ? 'مُرسَل' : log.status === 'failed' ? 'فشل' : 'تخطى'}
                    </span>
                    <span className="aem-log-date" dir="ltr">
                      {log.sent_at ? new Date(log.sent_at).toLocaleDateString('ar-EG') : '—'}
                    </span>
                  </div>
                  <div className="aem-log-email" dir="ltr">{log.email}</div>
                  <div className="aem-log-subject">{log.subject}</div>
                  <code className="aem-rule-key">{log.type}</code>
                  {log.error_message && (
                    <div className="aem-log-error-msg">{log.error_message}</div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      {/* ====== Edit Modal ====== */}
      {editModal && (
        <div className="aem-overlay" onClick={() => setEditModal(null)}>
          <div className="aem-modal" onClick={e => e.stopPropagation()} dir="rtl">
            <div className="aem-modal-header">
              <h3 className="aem-modal-title">تعديل: {editModal.rule.title}</h3>
              <button className="aem-modal-close" onClick={() => setEditModal(null)}>✕</button>
            </div>

            <div className="aem-modal-body">
              {/* Basic settings */}
              <div className="aem-form-grid">
                <div className="aem-form-row">
                  <label className="aem-label">تأخير الإرسال (ساعة)</label>
                  <input
                    type="number"
                    min="1"
                    className="aem-input"
                    value={editForm.delay_hours}
                    onChange={e => setEditForm(p => ({ ...p, delay_hours: e.target.value }))}
                  />
                </div>
                <div className="aem-form-row">
                  <label className="aem-label">حد الإرسال الأقصى</label>
                  <input
                    type="number"
                    min="1"
                    className="aem-input"
                    value={editForm.max_sends}
                    onChange={e => setEditForm(p => ({ ...p, max_sends: e.target.value }))}
                  />
                </div>
              </div>

              <div className="aem-form-row">
                <label className="aem-label">
                  Brevo Template ID <span className="aem-optional">(اختياري — له أولوية على HTML)</span>
                </label>
                <input
                  type="number"
                  className="aem-input"
                  placeholder="رقم القالب"
                  value={editForm.brevo_template_id}
                  onChange={e => setEditForm(p => ({ ...p, brevo_template_id: e.target.value }))}
                  dir="ltr"
                />
              </div>

              <div className="aem-form-row">
                <label className="aem-label">موضوع الإيميل</label>
                <input
                  type="text"
                  className="aem-input"
                  value={editForm.subject}
                  onChange={e => setEditForm(p => ({ ...p, subject: e.target.value }))}
                />
              </div>

              <div className="aem-form-row">
                <label className="aem-label">
                  محتوى HTML
                  <span className="aem-optional"> (اتركه فارغاً للمحتوى الافتراضي)</span>
                </label>
                <div className="aem-vars-bar">
                  {TEMPLATE_VARS.map(({ v, d }) => (
                    <code
                      key={v}
                      className="aem-var-chip"
                      title={d}
                      onClick={() => {
                        setEditForm(p => ({ ...p, body_html: (p.body_html || '') + v }));
                      }}
                    >
                      {v}
                    </code>
                  ))}
                </div>
                <p className="aem-html-warning">⚠️ سيتم إرسال هذا HTML مباشرةً عبر البريد الإلكتروني. تأكد من صحته.</p>
                <textarea
                  className="aem-textarea aem-textarea--code"
                  rows={8}
                  value={editForm.body_html}
                  onChange={e => setEditForm(p => ({ ...p, body_html: e.target.value }))}
                  dir="ltr"
                  placeholder="<p>مرحباً {{teacher_name}}،</p><p>...</p>"
                />
              </div>

              {editForm.body_html && (
                <div className="aem-form-row">
                  <button
                    className="aem-btn aem-btn--preview"
                    type="button"
                    onClick={() => setShowPreview(p => !p)}
                  >
                    {showPreview ? 'إخفاء المعاينة' : 'معاينة البريد'}
                  </button>
                  {showPreview && (
                    <div
                      className="aem-preview"
                      /* eslint-disable-next-line react/no-danger */
                      dangerouslySetInnerHTML={{ __html: previewHtml(editForm.body_html) }}
                    />
                  )}
                </div>
              )}

              <div className="aem-form-grid">
                <div className="aem-form-row">
                  <label className="aem-label">نص زر الإجراء <span className="aem-optional">(اختياري)</span></label>
                  <input
                    type="text"
                    className="aem-input"
                    placeholder="مثال: ابدأ الآن"
                    value={editForm.cta_label}
                    onChange={e => setEditForm(p => ({ ...p, cta_label: e.target.value }))}
                  />
                </div>
                <div className="aem-form-row">
                  <label className="aem-label">رابط زر الإجراء <span className="aem-optional">(اختياري)</span></label>
                  <input
                    type="url"
                    className="aem-input"
                    placeholder="https://..."
                    value={editForm.cta_url}
                    onChange={e => setEditForm(p => ({ ...p, cta_url: e.target.value }))}
                    dir="ltr"
                  />
                </div>
              </div>

              <div className="aem-form-row">
                <label className="aem-label">نص بديل للبريد <span className="aem-optional">(اختياري)</span></label>
                <textarea
                  className="aem-textarea"
                  rows={3}
                  value={editForm.body_text}
                  onChange={e => setEditForm(p => ({ ...p, body_text: e.target.value }))}
                  placeholder="نسخة نصية بسيطة من الرسالة..."
                />
              </div>

              <div className="aem-form-row">
                <label className="aem-checkbox-label">
                  <input
                    type="checkbox"
                    checked={editForm.enabled}
                    onChange={e => setEditForm(p => ({ ...p, enabled: e.target.checked }))}
                  />
                  <span>القاعدة مفعّلة</span>
                </label>
              </div>
            </div>

            <div className="aem-modal-footer">
              <button className="aem-btn aem-btn--cancel" onClick={() => setEditModal(null)}>
                إلغاء
              </button>
              <button className="aem-btn aem-btn--save" onClick={handleSaveEdit} disabled={saving}>
                {saving ? 'جاري الحفظ...' : 'حفظ التعديلات'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivationEmailsAdmin;
